/**
 * LLM Output Sanitisation Utilities
 * 
 * Security (P7):
 * - XSS protection for prose content
 * - Safe rendering of code blocks
 * - DOMPurify-based sanitisation
 */

// Simple HTML entity encoding for basic XSS protection
const escapeHtml = (str) => {
  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return str.replace(/[&<>"']/g, (char) => htmlEntities[char] || char);
};

// Remove script tags and event handlers
const removeScripts = (html) => {
  // Remove script tags
  let cleaned = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  // Remove event handlers
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  // Remove javascript: URLs
  cleaned = cleaned.replace(/javascript:/gi, '');
  return cleaned;
};

/**
 * Sanitise LLM prose output for safe HTML rendering.
 * 
 * Use this for insights, summaries, chat responses, etc.
 * Strips potentially dangerous HTML while preserving safe formatting.
 * 
 * @param content - Raw LLM output
 * @returns Sanitised string safe for innerHTML
 */
export const sanitiseLLMProse = (content) => {
  if (!content) return '';
  
  // First pass: remove scripts and event handlers
  let sanitised = removeScripts(content);
  
  // Allow safe tags only
  const allowedTags = ['p', 'br', 'b', 'i', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 'a', 'span', 'div'];
  
  // Remove disallowed tags (keep content)
  const tagPattern = /<\/?([a-z][a-z0-9]*)[^>]*>/gi;
  sanitised = sanitised.replace(tagPattern, (match, tagName) => {
    if (allowedTags.includes(tagName.toLowerCase())) {
      // For anchor tags, ensure href is safe
      if (tagName.toLowerCase() === 'a') {
        // Remove any href that's not http/https
        if (!/href\s*=\s*["']https?:\/\//i.test(match)) {
          return match.replace(/href\s*=\s*["'][^"']*["']/gi, '');
        }
      }
      return match;
    }
    return ''; // Remove disallowed tag
  });
  
  return sanitised;
};

/**
 * Sanitise LLM code output for safe display.
 * 
 * Use this for scripts, ARM templates, and other code outputs.
 * HTML-encodes the content for safe rendering in <pre><code> blocks.
 * 
 * @param content - Raw code output from LLM
 * @returns Sanitised string safe for code display
 */
export const sanitiseLLMCode = (content) => {
  if (!content) return '';
  
  // Extract code from markdown code blocks if present
  const codeBlockMatch = content.match(/```(?:\w+)?\s*([\s\S]*?)\s*```/);
  const code = codeBlockMatch ? codeBlockMatch[1] : content;
  
  // HTML-encode for safe display in <pre><code>
  return escapeHtml(code);
};

/**
 * Check if content contains potential injection patterns.
 * 
 * Use this for client-side warning/logging (server does the actual blocking).
 * 
 * @param content - User input to check
 * @returns true if suspicious patterns detected
 */
export const containsInjectionPatterns = (content) => {
  const patterns = [
    /ignore\s+(previous|above|all|prior)\s+(instructions?|prompts?)/i,
    /disregard\s+(the\s+)?(above|previous|all)/i,
    /you\s+are\s+(now|actually|really)\s+/i,
    /act\s+as\s+(if\s+you\s+are|a|an)\s+/i,
    /system\s+prompt/i,
    /<\s*system\s*>/i,
    /<\s*instruction\s*>/i,
  ];
  
  return patterns.some(pattern => pattern.test(content));
};

/**
 * Truncate content to a maximum length with ellipsis.
 * 
 * @param content - Content to truncate
 * @param maxLength - Maximum length (default 2000)
 * @returns Truncated string
 */
export const truncateForLLM = (content, maxLength = 2000) => {
  if (!content || content.length <= maxLength) return content;
  return content.substring(0, maxLength - 3) + '...';
};

export default {
  sanitiseLLMProse,
  sanitiseLLMCode,
  containsInjectionPatterns,
  truncateForLLM,
};
